import { inject, injectable } from "inversify";
import { VideoLayer } from "../../layer/VideoLayer";
import { TYPES } from "../../di/types";
import { BrowserFeatureDetector } from "../../../utils/BrowserFeatureDetector";
import { Soundtrack } from "../../../model/playerConfiguration/Soundtrack";

type AudioState = "notPlaying" | "playing" | "fadingIn" | "fadingOut";

export interface MusicManager {
  play(): void;

  pause(): void;

  changeSource(src: string): void;

  load(soundtrack: Soundtrack);
}

@injectable()
export class BaseMusicManager implements MusicManager {

  startOffset: number = 0;
  endOffset: number = 0;
  fadeInDuration: number = 0;
  fadeOutDuration: number = 0;

  private static UPDATE_INTERVAL = 30;

  private audio: HTMLAudioElement;

  private shouldBePlaying: boolean = false;

  private previousState: AudioState = "notPlaying";

  private initialized = false;

  private get endTime() {
    return this.videoLayer.getTotalTime() - this.endOffset - 0.1;
  }

  private get startTime() {
    return this.startOffset;
  }

  private get videoTime() {
    return this.videoLayer.getCurrentSeekTime();
  }

  constructor(
        @inject(TYPES.layer.VideoLayer) protected videoLayer: VideoLayer
    ) {
    this.audio = new Audio();
    this.audio.volume = 0;

    if (BrowserFeatureDetector.isIOS()) {
      this.installSafariWorkaround();
    }

  }

  changeSource(src: string): void {
    this.audio.src = src;
    this.audio.load();
  }

  load(soundtrack: Soundtrack) {
    this.endOffset = soundtrack.endOffset;
    this.startOffset = soundtrack.startOffset;
    this.fadeInDuration = soundtrack.fadeInDuration;
    this.fadeOutDuration = soundtrack.fadeOutDuration;

    this.changeSource(soundtrack.musicUrl);
  }

  pause(): void {
    this.shouldBePlaying = false;
  }

  play(): void {
    this.sync();

    this.shouldBePlaying = true;

    if (!this.initialized) {
        // We want to install it on the first play, because player can be in uninitialized state
        // and if we try to get currentTime or volume it will throw an error
      this.installLoop();

      this.initialized = true;
    }
  }

  private sync(maxAcceptableTimeDifference: number = 0): void {
    const desiredAudioTime = this.videoTime - this.startOffset;

    if (Math.abs(this.audio.currentTime - desiredAudioTime) > maxAcceptableTimeDifference) {
      this.audio.currentTime = desiredAudioTime;
    }
  }

  private calculateAudioState(): AudioState {
    const videoTime = this.videoTime;

    if (!this.shouldBePlaying || videoTime < this.startTime || videoTime >= this.endTime) {
      return "notPlaying";
    } else if (videoTime >= this.startTime && videoTime < this.startTime + this.fadeInDuration) {
      return "fadingIn";
    } else if (videoTime > this.endTime - this.fadeOutDuration && videoTime < this.endTime) {
      return "fadingOut";
    } else {
      return "playing";
    }
  }

  private doUpdate(): void {
    const state = this.calculateAudioState();

    if (state === "notPlaying") {
      if (!this.audio.paused) {
        this.shouldBePlaying = false;
        this.audio.pause();
      }
    } else {
      let volume = this.videoLayer.getVolume();

      if (state === "fadingIn") {
        volume = (this.videoTime - this.startTime) / this.fadeInDuration * this.videoLayer.getVolume();
      }

      if (state === "fadingOut") {
        volume = (this.endTime - this.videoTime) / this.fadeOutDuration * this.videoLayer.getVolume();
      }

      this.audio.volume = volume;

      if (this.audio.paused && this.videoLayer.getReadyState() > 2) {
        this.sync();

        this.audio.play();
      }

    }

    this.previousState = state;
  }

  private installLoop() {
    setInterval(this.doUpdate.bind(this), BaseMusicManager.UPDATE_INTERVAL);

    this.doUpdate();
  }

  /**
   * Safari browsers require the first audio play to be a result of user interaction
   */
  private installSafariWorkaround() {
    const initializeAudioPlayer = () => {
      this.audio.volume = 0;
      this.audio.play().then(() => {
        this.audio.pause();
        this.sync();
      });

      window.removeEventListener("click", initializeAudioPlayer);
    };

    window.addEventListener("click", initializeAudioPlayer);
  }

}
